import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { ContaReceber } from "@/hooks/useContasReceber";
import { usePacientes } from "@/hooks/usePacientes";
import { parseLocalDate, todayLocalDate } from "@/lib/date-utils";
import { calculatePaymentFee, isCardPayment } from "@/lib/paymentFees";

interface ContaReceberFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => Promise<void>;
  conta?: ContaReceber | null;
}

const categorias = [
  "Consulta",
  "Tratamento",
  "Ortodontia",
  "Implante",
  "Prótese",
  "Clareamento",
  "Limpeza",
  "Extração",
  "Canal",
  "Outros"
];

const formasPagamento = [
  "Dinheiro",
  "PIX",
  "Cartão de Crédito",
  "Cartão de Débito",
  "Boleto",
  "Transferência"
];

export function ContaReceberForm({ open, onOpenChange, onSubmit, conta }: ContaReceberFormProps) {
  const { pacientes } = usePacientes();

  const [formData, setFormData] = useState({
    paciente_id: "",
    descricao: "",
    categoria: "",
    valor: 0,
    data_vencimento: todayLocalDate(),
    data_recebimento: undefined as Date | undefined,
    status: "Pendente" as "Pendente" | "Recebida" | "Vencida" | "Cancelada",
    forma_pagamento: "",
    taxa_operadora_percentual: 0,
    observacoes: "",
  });

  useEffect(() => {
    if (conta) {
      setFormData({
        paciente_id: conta.paciente_id || "",
        descricao: conta.descricao,
        categoria: conta.categoria,
        valor: conta.valor,
        data_vencimento: parseLocalDate(conta.data_vencimento),
        data_recebimento: conta.data_recebimento ? parseLocalDate(conta.data_recebimento) : undefined,
        status: conta.status,
        forma_pagamento: conta.forma_pagamento || "",
        taxa_operadora_percentual: Number(conta.taxa_operadora_percentual || 0),
        observacoes: conta.observacoes || "",
      });
    } else {
      setFormData({
        paciente_id: "",
        descricao: "",
        categoria: "",
        valor: 0,
        data_vencimento: todayLocalDate(),
        data_recebimento: undefined,
        status: "Pendente",
        forma_pagamento: "",
        taxa_operadora_percentual: 0,
        observacoes: "",
      });
    }
  }, [conta]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const fee = calculatePaymentFee(formData.valor, formData.forma_pagamento, formData.taxa_operadora_percentual);
    const submitData = {
      ...formData,
      ...fee,
      paciente_id: formData.paciente_id || undefined,
      data_vencimento: format(formData.data_vencimento, 'yyyy-MM-dd'),
      data_recebimento: formData.data_recebimento ? format(formData.data_recebimento, 'yyyy-MM-dd') : undefined,
      forma_pagamento: formData.forma_pagamento || undefined,
      observacoes: formData.observacoes || undefined,
    };

    await onSubmit(submitData);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {conta ? "Editar Conta a Receber" : "Nova Conta a Receber"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="paciente_id">Paciente (opcional)</Label>
              <Select value={formData.paciente_id || undefined} onValueChange={(value) => setFormData({ ...formData, paciente_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um paciente" />
                </SelectTrigger>
                <SelectContent>
                  {pacientes.map((paciente) => (
                    <SelectItem key={paciente.id} value={paciente.id}>
                      {paciente.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Ex: Tratamento ortodôntico"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="categoria">Categoria *</Label>
              <Select value={formData.categoria} onValueChange={(value) => setFormData({ ...formData, categoria: value })} required>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="valor">Valor (R$) *</Label>
              <MoneyInput
                id="valor"
                value={formData.valor}
                onValueChange={(valor) => setFormData({ ...formData, valor })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Data de Vencimento *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.data_vencimento && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.data_vencimento ? format(formData.data_vencimento, "PPP", { locale: ptBR }) : <span>Selecione a data</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.data_vencimento}
                    onSelect={(date) => date && setFormData({ ...formData, data_vencimento: date })}
                    locale={ptBR}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status *</Label>
              <Select value={formData.status} onValueChange={(value: any) => setFormData({ ...formData, status: value })} required>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pendente">Pendente</SelectItem>
                  <SelectItem value="Recebida">Recebida</SelectItem>
                  <SelectItem value="Vencida">Vencida</SelectItem>
                  <SelectItem value="Cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(formData.status === "Recebida" || formData.data_recebimento) && (
              <>
                <div className="space-y-2">
                  <Label>Data de Recebimento</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.data_recebimento && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.data_recebimento ? format(formData.data_recebimento, "PPP", { locale: ptBR }) : <span>Selecione a data</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.data_recebimento}
                        onSelect={(date) => setFormData({ ...formData, data_recebimento: date })}
                        locale={ptBR}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="forma_pagamento">Forma de Recebimento</Label>
                  <Select value={formData.forma_pagamento} onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {formasPagamento.map((forma) => (
                        <SelectItem key={forma} value={forma}>
                          {forma}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isCardPayment(formData.forma_pagamento) && (
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="taxa_operadora_percentual">Taxa da operadora (%)</Label>
                    <Input
                      id="taxa_operadora_percentual"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.taxa_operadora_percentual}
                      onChange={(event) => setFormData({ ...formData, taxa_operadora_percentual: Number(event.target.value) || 0 })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Taxa {calculatePaymentFee(formData.valor, formData.forma_pagamento, formData.taxa_operadora_percentual).valor_taxa_operadora.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} · Valor líquido {calculatePaymentFee(formData.valor, formData.forma_pagamento, formData.taxa_operadora_percentual).valor_liquido.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="space-y-2 col-span-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              {conta ? "Atualizar" : "Cadastrar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
